import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import XIcon from "@mui/icons-material/X";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import BugReportIcon from "@mui/icons-material/BugReport";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import SeoHead from "../components/SeoHead";

export default function ContactPage() {
  return (
    <Box id="contact-page" sx={{ maxWidth: 800, mx: "auto", mt: 4 }}>
      <SeoHead
        title="Contact Us"
        description="Get in touch with the Music Brackets team. Report bugs, suggest features, ask questions, or just say hello."
        canonical="/contact"
      />

      <Typography variant="h4" textAlign="center" gutterBottom>
        <QuestionAnswerIcon
          sx={{ fontSize: 36, mr: 1, verticalAlign: "middle", color: "#3498db" }}
        />
        Contact Us
      </Typography>
      <Typography
        variant="body1"
        textAlign="center"
        color="text.secondary"
        sx={{ mb: 4, maxWidth: 600, mx: "auto" }}
      >
        We would love to hear from you. Whether you have a question, a
        suggestion, a bug report, or just want to say hello, there are several
        ways to get in touch with the Music Brackets team.
      </Typography>

      {/* Contact Methods */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
          gap: 3,
          mb: 4,
        }}
      >
        <Card
          sx={{
            backgroundColor: "rgba(52,152,219,0.04)",
            border: "1px solid rgba(52,152,219,0.12)",
          }}
        >
          <CardContent sx={{ textAlign: "center" }}>
            <EmailIcon sx={{ fontSize: 44, color: "#3498db", mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              Email
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              For general inquiries, support requests, and partnership
              opportunities, email us directly. We aim to respond within 48
              hours.
            </Typography>
            <Box
              component="a"
              href="mailto:support@musicrandomizer.com"
              sx={{
                color: "#3498db",
                textDecoration: "none",
                fontWeight: "bold",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              support@musicrandomizer.com
            </Box>
          </CardContent>
        </Card>

        <Card
          sx={{
            backgroundColor: "rgba(108,70,117,0.04)",
            border: "1px solid rgba(108,70,117,0.12)",
          }}
        >
          <CardContent sx={{ textAlign: "center" }}>
            <XIcon sx={{ fontSize: 44, color: "#6C4675", mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              X (Twitter)
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Follow us on X for updates, new features, and community
              highlights. Send us a DM or mention for a quick response.
            </Typography>
            <Box
              component="a"
              href="https://x.com/musicrandomized"
              target="_blank"
              sx={{
                color: "#6C4675",
                textDecoration: "none",
                fontWeight: "bold",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              @musicrandomized
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* What to Contact About */}
      <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.08)" }} />
      <Typography variant="h5" textAlign="center" gutterBottom>
        How Can We Help?
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        textAlign="center"
        sx={{ mb: 3, maxWidth: 600, mx: "auto" }}
      >
        Here are some common reasons people reach out to us. No matter the
        topic, we are happy to hear from you.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          gap: 2,
          mb: 4,
        }}
      >
        <Card sx={{ backgroundColor: "rgba(255,255,255,0.02)" }}>
          <CardContent sx={{ textAlign: "center" }}>
            <BugReportIcon sx={{ fontSize: 36, color: "#e74c3c", mb: 1 }} />
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Bug Reports
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Found something broken? Let us know what happened, what page you
              were on, and what browser you are using. Screenshots are always
              helpful.
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ backgroundColor: "rgba(255,255,255,0.02)" }}>
          <CardContent sx={{ textAlign: "center" }}>
            <LightbulbIcon sx={{ fontSize: 36, color: "#f39c12", mb: 1 }} />
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Feature Requests
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Have an idea for a new feature or improvement? We love hearing
              from our users. Tell us what would make Music Brackets better for
              you.
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ backgroundColor: "rgba(255,255,255,0.02)" }}>
          <CardContent sx={{ textAlign: "center" }}>
            <HelpOutlineIcon sx={{ fontSize: 36, color: "#3498db", mb: 1 }} />
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              General Questions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Questions about how the platform works, your account, data
              privacy, or anything else? We are happy to help with any question,
              big or small.
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Additional Info */}
      <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.08)" }} />
      <Card
        sx={{
          mb: 4,
          backgroundColor: "rgba(52,152,219,0.04)",
          border: "1px solid rgba(52,152,219,0.12)",
        }}
      >
        <CardContent>
          <Typography variant="h6" gutterBottom>
            About Our Team
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Music Brackets is built and maintained by the team behind{" "}
            <Box
              component="a"
              href="https://www.musicrandomizer.com"
              target="_blank"
              sx={{ color: "primary.main" }}
            >
              MusicRandomizer.com
            </Box>
            , a suite of free tools for music discovery and engagement. We
            started building music tools because we are passionate music fans
            ourselves, and we wanted to create experiences that help people
            discover, rank, and share the music they love.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Our platform serves thousands of music fans who use brackets to rank
            their favorite artists, discover new songs, and connect with other
            listeners. We are a small team, but we are dedicated to making Music
            Brackets the best music ranking tool on the web.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We value every piece of feedback we receive. Your bug reports help us
            fix issues faster. Your feature requests shape our roadmap. And your
            kind words keep us motivated. Thank you for being part of the Music
            Brackets community.
          </Typography>
        </CardContent>
      </Card>

      {/* FAQ-style quick answers */}
      <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.08)" }} />
      <Typography variant="h5" textAlign="center" gutterBottom>
        Quick Answers
      </Typography>
      <Box sx={{ maxWidth: 700, mx: "auto", mb: 4 }}>
        {[
          {
            q: "How quickly will I get a response?",
            a: "We aim to respond to all emails within 48 hours. For urgent issues, reaching out on X (Twitter) may get a faster response.",
          },
          {
            q: "I found a bug. What information should I include?",
            a: "Please include what you were doing when the bug occurred, what page you were on, your browser and device, and a screenshot if possible. The more detail you provide, the faster we can fix it.",
          },
          {
            q: "Can I request a specific artist to be added?",
            a: "Yes! We are always expanding our database. Send us the artist name and we will look into adding them. Popular requests are prioritized.",
          },
          {
            q: "How do I delete my account data?",
            a: "Send us an email at support@musicrandomizer.com with your request and the email address associated with your Google sign-in. We will process your request promptly.",
          },
          {
            q: "Can I use Music Brackets for my podcast or stream?",
            a: "Absolutely. Music Brackets is free to use for personal and content creation purposes. We love seeing brackets on streams and podcasts. Enable Streamer Mode in Settings to hide your last name.",
          },
        ].map((item) => (
          <Card
            key={item.q}
            sx={{
              mb: 1.5,
              backgroundColor: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                {item.q}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.a}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
